import React from 'react';
import { Player, PlayerHistoryRecord, GameState } from '../../../domain/entities';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';
import { TeamBadge } from '../../shared/TeamBadge';

interface PlayerCareerHistoryPanelProps {
    player: Player;
    gameState: GameState;
    teamName: string;
    pushView: (view: View) => void;
}

export const PlayerCareerHistoryPanel: React.FC<PlayerCareerHistoryPanelProps> = ({ player, gameState, teamName, pushView }) => {
    const careerHistoryForDisplay = React.useMemo(() => {
        const currentSeasonStats: PlayerHistoryRecord = { 
            season: gameState.season, 
            teamName: teamName, 
            skill: player.skill, 
            gamesPlayed: player.gamesPlayed, 
            goalsScored: player.goalsScored, 
            goalsConceded: player.goalsConceded, 
            assists: player.assists, 
            redCards: player.redCards, 
            yellowCards: player.yellowCards, 
            potmAwards: player.potmAwards 
        };
        return [...(player.history || []), currentSeasonStats];
    }, [player, gameState.season, teamName]);

    const getTeamByName = (name: string) => {
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    };

    const handleTeamClick = (teamName: string) => {
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName: teamName });
    };

    return (
        <div className="panel">
            <div className="panel-header">Career History</div>
            <div className="panel-content">
                {careerHistoryForDisplay.length > 0 ? (
                    <div className="table-wrapper" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        <table className="table" style={{ fontSize: '0.9em' }}>
                            <thead>
                                <tr>
                                    <th>S</th>
                                    <th>Team</th>
                                    <th>P</th>
                                    <th>G</th>
                                    {player.position === 'GK' && <th>GC</th>}
                                    <th>A</th>
                                    <th style={{color: '#ffc107'}}>Y</th>
                                    <th style={{color: '#ff4d4d'}}>R</th>
                                    <th>⭐</th>
                                </tr>
                            </thead>
                            <tbody>
                            {[...careerHistoryForDisplay].reverse().map(record => (
                                <tr key={`${record.season}-${record.teamName}`}>
                                    <td>{record.season}{record.season === gameState.season ? '*' : ''}</td>
                                    <td title={record.teamName} style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                        <button className="link-style" onClick={() => handleTeamClick(record.teamName)}>
                                            <span className={record.teamName === gameState.playerTeamName ? 'player-team-name' : ''}>{getTeamByName(record.teamName)?.badge && <TeamBadge badge={getTeamByName(record.teamName)?.badge} color={getTeamByName(record.teamName)?.colors.primary} size="sm" />}{record.teamName}</span>
                                        </button>
                                    </td>
                                    <td>{record.gamesPlayed}</td>
                                    <td>{record.goalsScored}</td>
                                    {player.position === 'GK' && <td>{record.goalsConceded}</td>}
                                    <td>{record.assists}</td>
                                    <td style={{color: '#ffc107'}}>{record.yellowCards}</td>
                                    <td style={{color: '#ff4d4d'}}>{record.redCards}</td>
                                    <td>{record.potmAwards}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                         <p style={{fontSize: '0.8em', opacity: 0.7, marginTop: '8px', padding: '0 4px'}}>* Current season. S=Season, P=Played, G=Goals, GC=Goals Conceded, A=Assists, Y=Yellow Cards, R=Red Cards, ⭐=Player of the Match</p>
                    </div>
                ) : (
                    <p className="text-secondary">No career history recorded yet.</p>
                )}
            </div>
        </div>
    );
};